import PropTypes from 'prop-types'
import { NotificationOutlined } from '@ant-design/icons'
import { Button, Divider, message, Modal, Select, Space } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import startCase from 'lodash/startCase'
import api from '../../api'

const TYPE_LABELS = {
  connectivity: 'Connectivity',
  deactivation: 'Deactivation',
  info_request: 'Information Request',
}

const SUBTYPE_CONTENT = {
  confirmation: {
    title: 'Connectivity Confirmation',
    body: "This message is to confirm that we have successfully received the patient's transmission and the monitor is now connected. No further action is required at this time.",
  },
  contact_information: {
    title: 'Contact Information Request',
    body: 'We are requesting updated contact information to ensure continued remote monitoring services. Please provide the requested information at your earliest convenience.',
  },
  insurance_information: {
    title: 'Insurance Information Request',
    body: 'We are requesting updated insurance information to ensure continued remote monitoring services. Please provide the requested information at your earliest convenience.',
  },
}

export default function ConnectivityNotificationModal({
  patientId,
  visible,
  onCancel,
  onSuccess = undefined,
}) {
  const [options, setOptions] = useState([])
  const [selected, setSelected] = useState(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!visible) return

    setLoading(true)
    api.notifications
      .getReasons()
      .then(reasons => {
        setOptions(
          reasons
            .filter(r => r.type !== 'deactivation')
            .map(({ type, subTypes }) => ({
              label: TYPE_LABELS[type] || startCase(type),
              options: subTypes.map(subType => ({
                value: `${type}:${subType}`,
                label: SUBTYPE_CONTENT[subType]?.title || startCase(subType),
              })),
            })),
        )
      })
      .catch(() => message.error('Could not load notification types'))
      .finally(() => setLoading(false))
  }, [visible])

  const handleCancel = useCallback(() => {
    setSelected(null)
    onCancel()
  }, [onCancel])

  const handleSubmit = useCallback(async () => {
    if (!selected) return

    const [notificationType, subType] = selected.split(':')
    const content = SUBTYPE_CONTENT[subType]

    setSubmitting(true)
    try {
      await api.notifications.create({
        patientId,
        notificationType,
        subType,
        title: content?.title || startCase(subType),
        body: content?.body || null,
      })
      message.success('Notification created successfully')
      setSelected(null)
      onCancel()
      onSuccess?.()
    } catch (e) {
      message.error('Could not create notification. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }, [patientId, selected, onCancel, onSuccess])

  return (
    <Modal
      title={
        <Space>
          <NotificationOutlined />
          Create Notification
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <p>Select the type of notification to generate for this patient.</p>
      <Select
        placeholder="Select notification type"
        style={{ width: '100%' }}
        value={selected}
        onChange={setSelected}
        loading={loading}
        options={options}
      />
      <Divider />
      <div style={{ textAlign: 'right' }}>
        <Space>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!selected}
          >
            Create Notification
          </Button>
        </Space>
      </div>
    </Modal>
  )
}

ConnectivityNotificationModal.propTypes = {
  patientId: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
}
